import { Outlet, useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { ArrowRight, Leaf } from 'lucide-react'
import { useState, useEffect } from 'react'
import Header from './Header'
import Footer from './Footer'
import FloatingLinks from './FloatingLinks'

const Layout = ({ children }) => {
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  
  // Array of hero images
  const heroImages = [
    '/media/images/hero/image1.png',
    '/media/images/hero/image2.png',
    '/media/images/hero/image3.png',
    '/media/images/hero/image4.png',
    '/media/images/hero/image5.png',
    '/media/images/hero/image6.png',
    '/media/images/hero/image7.png'
  ]

  // Auto-rotate images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % heroImages.length
      )
    }, 4000)
    
    return () => clearInterval(interval)
  }, [heroImages.length])
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero Section - Only show on home page */}
        {isHomePage && (
          <section className="relative h-screen flex items-center justify-center overflow-hidden">
            {/* Background Image - no overlay so images stay clear */}
            <div className="absolute inset-0" style={{ zIndex: 1 }}>
              <img
                src={heroImages[currentImageIndex]}
                alt="Hero background"
                className="w-full h-full object-cover object-center"
                onLoad={() => {}}
                onError={(e) => {
                  console.error('❌ Failed to load image:', heroImages[currentImageIndex])
                  e.target.style.display = 'none'
                }}
              />
              {/* Very subtle gradient only at bottom under the card, so image stays visible */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" style={{ height: '100%' }} />
            </div>

            {/* Fallback Background - Only show if image fails */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-100 via-pink-50 to-gray-100" style={{ display: 'none', zIndex: 1 }} />

            {/* Decorative accents */}
            <div className="absolute top-16 left-8 sm:left-12 opacity-80" style={{ zIndex: 2 }}>
              <Leaf className="w-6 h-6 sm:w-8 sm:h-8 text-pink-200/90 animate-pulse" />
            </div>
            <div className="absolute bottom-40 right-8 sm:right-12 opacity-80" style={{ zIndex: 2 }}>
              <Leaf className="w-5 h-5 sm:w-6 sm:h-6 text-white/70 animate-pulse" style={{ animationDelay: '1.5s' }} />
            </div>
            <div className="absolute top-1/3 right-12 sm:right-16 opacity-70" style={{ zIndex: 2 }}>
              <Leaf className="w-4 h-4 text-pink-100 animate-pulse" style={{ animationDelay: '3s' }} />
            </div>

            {/* Content: dark card for readability on phone (no blur, images stay clear) */}
            <div className="relative w-full max-w-4xl mx-auto px-4 pb-12 sm:pb-16 lg:pb-20" style={{ zIndex: 3 }}>
              <div className="bg-black/75 rounded-3xl border border-white/20 shadow-2xl p-5 sm:p-6 md:p-8 text-center">
                {/* Headline block */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 font-serif">
                  <span className="block text-white drop-shadow-md">Spa~Radise Luxe</span>
                  <span className="block text-pink-100 drop-shadow-md mt-1">Relax, Renew & Glow</span>
                  <span className="block text-white/90 text-lg sm:text-xl md:text-2xl font-normal mt-2 drop-shadow-md">A romantic escape just for you</span>
                </h1>

                <p className="text-sm sm:text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-5 sm:mb-6 drop-shadow-sm">
                  Spa~Radise Luxe blends soft lighting, calming music and thoughtful details
                  into curated spa rituals, from soothing massages to glowing facials,
                  body scrubs, manicures and pedicures.
                </p>

                {/* Quick links: single row, scroll on small screens */}
                <div className="flex flex-wrap justify-center gap-2 mb-5 sm:mb-6">
                  <Link to="/about" className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white/95 text-xs sm:text-sm font-medium transition-all hover:scale-105">
                    About
                  </Link>
                  <Link to="/services" className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white/95 text-xs sm:text-sm font-medium transition-all hover:scale-105">
                    Services
                  </Link>
                  <Link to="/gallery" className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white/95 text-xs sm:text-sm font-medium transition-all hover:scale-105">
                    Gallery
                  </Link>
                  <Link to="/blog" className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white/95 text-xs sm:text-sm font-medium transition-all hover:scale-105">
                    Blog
                  </Link>
                  <Link to="/help-center" className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white/95 text-xs sm:text-sm font-medium transition-all hover:scale-105">
                    Help Center
                  </Link>
                  <Link to="/gift-card" className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white/95 text-xs sm:text-sm font-medium transition-all hover:scale-105">
                    Gift Card
                  </Link>
                  <Link to="/contact" className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white/95 text-xs sm:text-sm font-medium transition-all hover:scale-105">
                    Contact
                  </Link>
                </div>

                {/* Primary CTAs: View Our Services (-> /services), Contact Us (-> /contact) */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center mb-5 sm:mb-6">
                  <Link
                    to="/services"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-400 hover:from-purple-600 hover:via-pink-600 hover:to-rose-500 text-white font-semibold px-5 py-2.5 sm:px-6 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                  >
                    <Leaf className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>View Our Services</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center border-2 border-white/80 text-white hover:bg-white/15 font-semibold px-5 py-2.5 sm:px-6 sm:py-3 rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                  >
                    Contact Us
                  </Link>
                </div>

              </div>
            </div>

            {/* Carousel dots anchored near bottom of hero images */}
            <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-1.5 sm:gap-2" style={{ zIndex: 4 }}>
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentImageIndex
                      ? 'w-6 sm:w-8 h-2 bg-white'
                      : 'w-2 h-2 bg-white/50 hover:bg-white/75'
                  }`}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </section>
        )}
        
        {/* Regular content for all pages */}
        <div className="relative min-h-screen" style={{ backgroundColor: '#F8F5FD', zIndex: 1 }}>
          {/* Romantic Spa Decorations */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Floating Flowers */}
            <div className="absolute top-20 left-5 w-16 h-16 opacity-15 animate-pulse" style={{ animationDuration: '4s' }}>
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 20 C55 15, 65 15, 70 20 C75 25, 75 35, 70 40 C75 45, 75 55, 70 60 C65 65, 55 65, 50 60 C45 65, 35 65, 30 60 C25 55, 25 45, 30 40 C25 35, 25 25, 30 20 C35 15, 45 15, 50 20 Z" fill="#C8B99C" stroke="#A67C52" strokeWidth="2"/>
                <circle cx="50" cy="40" r="8" fill="#FFD700"/>
              </svg>
            </div>
            <div className="absolute top-40 right-10 w-12 h-12 opacity-10 animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }}>
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 15 C58 10, 68 10, 75 15 C82 22, 82 32, 75 40 C78 45, 78 55, 75 60 C68 68, 58 68, 50 60 C42 68, 32 68, 25 60 C22 55, 22 45, 25 40 C18 32, 18 22, 25 15 C32 10, 42 10, 50 15 Z" fill="#D4A574" stroke="#8B6914" strokeWidth="2"/>
                <circle cx="50" cy="37" r="6" fill="#FFE5B4"/>
              </svg>
            </div>
            <div className="absolute bottom-32 left-20 w-14 h-14 opacity-12 animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}>
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 25 C56 20, 64 20, 70 25 C76 31, 76 39, 70 45 C74 49, 74 57, 70 61 C64 67, 56 67, 50 61 C44 67, 36 67, 30 61 C26 57, 26 49, 30 45 C24 39, 24 31, 30 25 C36 20, 44 20, 50 25 Z" fill="#E8D5B7" stroke="#C8B99C" strokeWidth="2"/>
                <circle cx="50" cy="43" r="7" fill="#FFD700"/>
              </svg>
            </div>
            
            {/* Sparkling Stars */}
            <div className="absolute top-32 right-1/4 w-3 h-3 opacity-30 animate-pulse" style={{ animationDuration: '2s' }}>
              <svg viewBox="0 0 24 24" fill="#FFD700">
                <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z"/>
              </svg>
            </div>
            <div className="absolute top-60 left-1/3 w-2 h-2 opacity-25 animate-pulse" style={{ animationDuration: '3s', animationDelay: '1s' }}>
              <svg viewBox="0 0 24 24" fill="#FFE5B4">
                <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z"/>
              </svg>
            </div>
            <div className="absolute bottom-48 right-1/3 w-2.5 h-2.5 opacity-20 animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}>
              <svg viewBox="0 0 24 24" fill="#FFD700">
                <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z"/>
              </svg>
            </div>
            <div className="absolute top-80 right-16 w-2 h-2 opacity-28 animate-pulse" style={{ animationDuration: '2.8s', animationDelay: '1.5s' }}>
              <svg viewBox="0 0 24 24" fill="#FFE5B4">
                <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z"/>
              </svg>
            </div>

            {/* Elegant Leaves */}
            <div className="absolute top-52 left-12 w-20 h-20 opacity-10 transform rotate-12">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 10 C30 10, 15 25, 15 45 C15 55, 20 65, 30 75 C40 85, 50 90, 60 85 C70 80, 80 70, 85 60 C90 50, 90 40, 85 30 C80 20, 70 15, 60 12 C55 10, 52 10, 50 10 Z" fill="#7FB069" stroke="#5A8F4A" strokeWidth="2"/>
              </svg>
            </div>
            <div className="absolute bottom-40 right-8 w-16 h-16 opacity-8 transform -rotate-45">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 15 C35 15, 20 30, 20 45 C20 52, 23 59, 28 65 C33 71, 40 75, 48 73 C56 71, 65 65, 70 58 C75 51, 78 42, 75 35 C72 28, 65 20, 55 18 C52 17, 51 16, 50 15 Z" fill="#7FB069" stroke="#5A8F4A" strokeWidth="2"/>
              </svg>
            </div>

            {/* Rose Petals */}
            <div className="absolute top-72 left-1/4 w-14 h-14 opacity-12 transform rotate-45">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 20 C45 20, 40 25, 40 30 C40 35, 45 40, 50 45 C55 40, 60 35, 60 30 C60 25, 55 20, 50 20 Z" fill="#E8B4B4" stroke="#D49797" strokeWidth="1.5"/>
                <path d="M50 25 C48 25, 46 27, 46 29 C46 31, 48 33, 50 35 C52 33, 54 31, 54 29 C54 27, 52 25, 50 25 Z" fill="#FFD700"/>
              </svg>
            </div>
            <div className="absolute bottom-60 left-16 w-10 h-10 opacity-10 transform -rotate-30">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 25 C47 25, 44 28, 44 31 C44 34, 47 37, 50 40 C53 37, 56 34, 56 31 C56 28, 53 25, 50 25 Z" fill="#E8B4B4" stroke="#D49797" strokeWidth="1.5"/>
                <circle cx="50" cy="32" r="3" fill="#FFD700"/>
              </svg>
            </div>

            {/* Lotus/Water Lily */}
            <div className="absolute top-1/4 right-1/3 w-20 h-20 opacity-8">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 20 L55 35 L70 40 L55 45 L50 60 L45 45 L30 40 L45 35 Z" fill="#F5E6D3" stroke="#E8D5B7" strokeWidth="2"/>
                <path d="M50 25 L53 35 L63 38 L53 41 L50 50 L47 41 L37 38 L47 35 Z" fill="#FFE5B4"/>
                <circle cx="50" cy="40" r="5" fill="#FFD700"/>
              </svg>
            </div>

            {/* Additional Small Sparkles */}
            <div className="absolute top-1/3 left-1/2 w-1.5 h-1.5 opacity-20 animate-pulse" style={{ animationDuration: '1.5s' }}>
              <svg viewBox="0 0 24 24" fill="#FFD700">
                <circle cx="12" cy="12" r="4"/>
              </svg>
            </div>
            <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 opacity-15 animate-pulse" style={{ animationDuration: '2s', animationDelay: '0.8s' }}>
              <svg viewBox="0 0 24 24" fill="#FFE5B4">
                <circle cx="12" cy="12" r="4"/>
              </svg>
            </div>
            <div className="absolute top-2/3 right-1/4 w-1.5 h-1.5 opacity-18 animate-pulse" style={{ animationDuration: '2.2s', animationDelay: '1.2s' }}>
              <svg viewBox="0 0 24 24" fill="#FFD700">
                <circle cx="12" cy="12" r="4"/>
              </svg>
            </div>
          </div>
          
          {/* Content */}
          <div className="relative" style={{ zIndex: 2 }}>
            {children}
          </div>
        </div>
      </main>
      <FloatingLinks />
      <Footer />
    </div>
  )
}

export default Layout
